import React, { useState } from "react";
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Space, 
  Typography,
  Divider,
  Alert
} from "@pankod/refine-antd";
import { IWorkPlan, WorkPlanStatusEnum } from "interfaces/work-plan.interface";

const { TextArea } = Input;
const { Text } = Typography;

interface WorkPlanReviewModalProps {
  visible: boolean;
  onCancel: () => void;
  onReview: (action: 'OBSERVATION' | 'AUTHORIZED', observation?: string) => void;
  workPlan?: IWorkPlan;
  isLoading?: boolean;
}

export const WorkPlanReviewModal: React.FC<WorkPlanReviewModalProps> = ({
  visible,
  onCancel,
  onReview,
  workPlan,
  isLoading = false,
}) => {
  const [form] = Form.useForm();
  const [reviewAction, setReviewAction] = useState<'OBSERVATION' | 'AUTHORIZED' | null>(null);

  const handleReview = async () => {
    if (!reviewAction) return;

    if (reviewAction === 'OBSERVATION') {
      try {
        const values = await form.validateFields();
        onReview(reviewAction, values.observation);
      } catch (error) {
        // Form validation failed
        return;
      }
    } else {
      onReview(reviewAction);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setReviewAction(null);
    onCancel();
  };

  const isObservationMode = reviewAction === 'OBSERVATION';
  const isAuthorizedMode = reviewAction === 'AUTHORIZED';

  return (
    <Modal
      title="Revisar Plan de Trabajo"
      visible={visible}
      onCancel={handleCancel}
      width={600}
      footer={null}
      destroyOnClose
    >
      {workPlan && (
        <div>
          <Alert
            message="Plan de trabajo enviado para revisión"
            description={`ID: ${workPlan.id} - ${workPlan.strategy}`}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          {!reviewAction && (
            <div>
              <Text strong>¿Qué acción deseas realizar?</Text>
              <Divider />
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => setReviewAction('OBSERVATION')}
                  style={{ height: '60px', textAlign: 'left' }}
                >
                  <div>
                    <div><strong>Solicitar Revisión</strong></div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Enviar observaciones para que el usuario corrija
                    </div>
                  </div>
                </Button>
                
                <Button
                  type="default"
                  size="large"
                  block
                  onClick={() => setReviewAction('AUTHORIZED')}
                  style={{ height: '60px', textAlign: 'left' }}
                >
                  <div>
                    <div><strong>Autorizar Actividad</strong></div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Aprobar el plan de trabajo tal como está
                    </div>
                  </div>
                </Button>
              </Space>
            </div>
          )}

          {isObservationMode && (
            <div>
              <Alert
                message="Solicitar Revisión"
                description="Agrega las observaciones necesarias para que el usuario pueda corregir el plan de trabajo."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Form form={form} layout="vertical">
                <Form.Item
                  name="observation"
                  label="Observaciones"
                  rules={[
                    { required: true, message: 'Por favor ingresa las observaciones necesarias' }
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Describe las correcciones necesarias..."
                    maxLength={500}
                    showCount
                  />
                </Form.Item>
              </Form>

              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setReviewAction(null)}>
                  Volver
                </Button>
                <Button 
                  type="primary" 
                  danger 
                  onClick={handleReview}
                  loading={isLoading}
                >
                  Enviar Observaciones
                </Button>
              </Space>
            </div>
          )}

          {isAuthorizedMode && (
            <div>
              <Alert
                message="Confirmar Autorización"
                description="¿Estás seguro de que deseas autorizar este plan de trabajo? Esta acción no se puede deshacer."
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setReviewAction(null)}>
                  Volver
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleReview}
                  loading={isLoading}
                >
                  Autorizar Plan
                </Button>
              </Space>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
